import React from 'react';
import { LocalizedLink } from 'src/components/common/LocalizedLink';

interface ImageData {
  url: string;
  caption?: string;
  caption_en?: string;
}

interface MagazineExampleProps {
  images?: ImageData[];
  imageSrc?: string;
  fallbackImage?: string; // フォールバック画像（オプション）
  bottomText?: React.ReactNode;
  imageTall?: boolean;
  bottomTextPosition?: 'center' | 'bottom-left';
  useEnglish?: boolean;
}

export const MagazineExample: React.FC<MagazineExampleProps> = ({
  images,
  imageSrc,
  fallbackImage,
  bottomText = null,
  imageTall = true,
  bottomTextPosition = 'center',
  useEnglish = false,
}) => {
  const imagesArray: ImageData[] =
    images && images.length > 0 ? images : imageSrc ? [{ url: imageSrc }] : [];

  const mainImage: ImageData =
    imagesArray.length > 0 ? imagesArray[0] : { url: fallbackImage || '' };
  const additionalImages: ImageData[] =
    imagesArray.length > 1 ? imagesArray.slice(1) : [];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (fallbackImage) {
      e.currentTarget.src = fallbackImage;
    }
  };

  return (
    <div className="relative w-full overflow-hidden">
      {/* SPサイズ: メイン画像 */}
      <div className="block md:hidden max-md:mx-5">
        <img
          src={mainImage.url}
          alt="Main Image"
          className={`w-full object-cover ${
            imageTall ? 'h-[610px]' : 'max-h-[450px]'
          }`}
          onError={handleImageError}
        />
        {mainImage.caption && (
          <div className="mt-2 text-start text-sm">
            {useEnglish
              ? mainImage.caption_en || mainImage.caption
              : mainImage.caption}
          </div>
        )}
        {bottomText && <div className="p-4">{bottomText}</div>}

        {/* — additionalImages がない場合はここにBack to Top */}
        {additionalImages.length === 0 && (
          <div className="mt-4 text-start">
            <LocalizedLink to="/magazine" className="underline">
              &lt; Back to Top
            </LocalizedLink>
          </div>
        )}
      </div>

      {/* MDサイズ以上: メイン画像 */}
      <div className="hidden md:flex w-full">
        <div className="w-1/2 flex flex-col items-end">
          <div className="w-[429px]">
            <img
              src={mainImage.url}
              alt="Main Image"
              className="w-full object-cover"
              onError={handleImageError}
            />
            {mainImage.caption && (
              <div className="mt-2 text-start text-sm">
                {useEnglish
                  ? mainImage.caption_en || mainImage.caption
                  : mainImage.caption}
              </div>
            )}
          </div>
        </div>
        <div className="w-1/2 relative">
          {bottomText && (
            <div
              className={`${
                bottomTextPosition === 'center'
                  ? 'flex flex-col items-start h-full ml-10'
                  : 'absolute bottom-0 left-0 p-4'
              }`}
            >
              {bottomText}
            </div>
          )}
        </div>
      </div>

      {/* 追加画像（2枚目以降） */}
      {additionalImages.length > 0 && (
        <>
          {/* SP: モバイル表示 */}
          <div className="block md:hidden mt-4 max-md:mx-5">
            {additionalImages.map((img, idx) => (
              <div key={idx} className="w-full mb-4">
                <img
                  src={img.url}
                  alt={`Additional Image ${idx + 2}`}
                  className="w-full h-[400px] object-cover"
                  onError={handleImageError}
                />
                {img.caption && (
                  <div className="mt-2 text-start text-sm">
                    {useEnglish ? img.caption_en || img.caption : img.caption}
                  </div>
                )}
              </div>
            ))}
            {/* Back to Top */}
            <div className="mt-4 text-start">
              <LocalizedLink to="/magazine" className="underline">
                &lt; Back to Top
              </LocalizedLink>
            </div>
          </div>

          {/* MD: デスクトップ表示 */}
          <div className="hidden md:flex mt-4">
            <div className="w-1/2 flex flex-col items-end space-y-4">
              {additionalImages.map((img, idx) => (
                <div key={idx} className="w-[429px]">
                  <img
                    src={img.url}
                    alt={`Additional Image ${idx + 2}`}
                    className="w-full object-cover"
                    onError={handleImageError}
                  />
                  {img.caption && (
                    <div className="mt-2 text-start text-sm">
                      {useEnglish ? img.caption_en || img.caption : img.caption}
                    </div>
                  )}
                </div>
              ))}
              {/* Back to Top */}
              <div className="self-end mt-4">
                <div className="w-[429px]">
                  <LocalizedLink to="/magazine" className="underline">
                    &lt; Back to Top
                  </LocalizedLink>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MagazineExample;
